import { Form, Icon, Input, Label } from 'semantic-ui-react';
import React, { useCallback, useEffect, useState } from 'react';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { useAlert } from 'context/AlertProvider';
import { getSingle } from 'services/pim.service';
import classNames from 'classnames';
import { EllipsisElement } from 'components/EllipsisElement/EllipsisElement';
import './article-replacer.scss';

export default function ArticleReplacer({
  article,
  hasReplacement,
  setLoading = () => {},
  onAddReplacement = () => {},
  onRemoveReplacement = () => {},
}) {
  const alert = useAlert();
  const [targetSKU, setTargetSKU] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [replacementArticle, setReplacementArticle] = useState();

  const findReplacement = useCallback(
    async (sku) => {
      try {
        setLoading(true);
        setNotFound(false);
        const { data: replacementArticle } = await getSingle(sku);

        setReplacementArticle(replacementArticle);
        onAddReplacement(article.sku, replacementArticle.sku);
      } catch (error) {
        if (error.response.data.detail.type === 'pim.not_found') {
          setNotFound(true);
        } else {
          alert.current.error(getMessageFromAxiosError(error));
        }
        onRemoveReplacement(article.sku);
      } finally {
        setLoading(false);
      }
    },
    [alert, article, onAddReplacement, onRemoveReplacement, setLoading],
  );

  useEffect(() => {
    (async () => {
      setNotFound(false);
    })();
  }, [targetSKU]);

  return (
    <article
      className={classNames('article-replacer', {
        'article-replacer--no-replacement-found': !hasReplacement,
      })}
    >
      <Form>
        <Form.Group inline>
          {!hasReplacement ? (
            <Icon
              name="warning sign"
              color="red"
              role="img"
              aria-label="No replacement found"
            />
          ) : (
            <Icon
              name="check"
              color="green"
              role="img"
              aria-label="Replacement found"
            />
          )}
          <Label>
            <h3>
              SKU <strong>{article.sku}</strong>
            </h3>
          </Label>
          <Icon name="angle right" color="grey" />
          <Form.Field>
            <label>Sustituir por</label>
            <Input
              action={{
                icon: 'search',
                compact: true,
                disabled: hasReplacement,
                onClick: () => findReplacement(targetSKU),
              }}
              disabled={hasReplacement}
              placeholder="Buscar SKU…"
              value={targetSKU}
              onChange={(event, { value }) => setTargetSKU(value)}
            />
          </Form.Field>
          {hasReplacement && !!replacementArticle?.sku && (
            <div className="article-replacer__replacement-info">
              <div>
                <h4>¡Sustitutivo encontrado!</h4>
                <code>
                  SKU <big>{replacementArticle.sku}</big> | EAN{' '}
                  <big>{replacementArticle.ean}</big>
                </code>
                {replacementArticle?.short_description && (
                  <EllipsisElement>
                    {replacementArticle.short_description}
                  </EllipsisElement>
                )}
              </div>
              <Icon
                role="img"
                aria-label="Cancel replacement"
                className="article-replacer__cancel-replacement"
                name="close"
                onClick={(event, data) => {
                  onRemoveReplacement(article.sku);
                  setTargetSKU('');
                }}
              />
            </div>
          )}
          {!!notFound && (
            <span style={{ color: 'red', margin: 0 }}>
              ¡No se encuentra el artículo sustitutivo!
            </span>
          )}
        </Form.Group>
      </Form>
    </article>
  );
}
